<!-- RemoveWhiteBg.vue -->
<template>
  <canvas ref="cv" :style="canvasStyle" />
</template>

<script>
export default {
  name: "RemoveWhiteBg",
  props: {
    src: { type: String, required: true },
    threshold: { type: Number, default: 245 },      // 240–252
    softness: { type: Number, default: 12 },         // 0–20 edge feather
    chromaTolerance: { type: Number, default: 24 },  // 0–40 protect colored highlights
    crossOrigin: { type: String, default: "anonymous" },
    maxRenderWidth: { type: Number, default: 1200 }, // scale huge photos down
    width: { type: [String, Number], default: "100%" }, // CSS size
    height: { type: [String, Number], default: "auto" }
  },
  computed: {
    canvasStyle() {
      return {
        display: "block",
        width: typeof this.width === "number" ? `${this.width}px` : this.width,
        height: typeof this.height === "number" ? `${this.height}px` : this.height,
        objectFit: "contain"
      };
    }
  },
  watch: {
    src: { handler() { this.process(); }, immediate: true },
    threshold() { this.process(); },
    softness() { this.process(); },
    chromaTolerance() { this.process(); }
  },
  methods: {
    process() {
      if (!this.src) return;
      const img = new Image();
      // Important for remote URLs; requires server to send CORS headers
      img.crossOrigin = this.crossOrigin;
      img.onload = () => {
        try {
          const cv = this.$refs.cv;
          const ctx = cv.getContext("2d");
          // Scale down if very large for perf
          const scale = this.maxRenderWidth
            ? Math.min(1, this.maxRenderWidth / img.naturalWidth || 1)
            : 1;
          const w = Math.max(1, Math.round(img.naturalWidth * scale));
          const h = Math.max(1, Math.round(img.naturalHeight * scale));
          cv.width = w;
          cv.height = h;

          ctx.drawImage(img, 0, 0, w, h);

          let imgData;
          try {
            imgData = ctx.getImageData(0, 0, w, h);
          } catch (e) {
            // Most likely a CORS/tainted canvas issue
            console.error("[RemoveWhiteBg] getImageData failed (CORS?). Drawing original image without removal.", e);
            // We at least draw the original (already done). Bail out gracefully.
            return;
          }

          const data = imgData.data;
          const t = Math.min(Math.max(this.threshold, 0), 255);
          const soft = Math.max(this.softness, 0);
          const cTol = Math.max(this.chromaTolerance, 0);

          for (let i = 0; i < data.length; i += 4) {
            const r = data[i], g = data[i+1], b = data[i+2];
            const maxc = Math.max(r, g, b);
            const minc = Math.min(r, g, b);
            const chroma = maxc - minc;

            if (chroma > cTol) continue; // keep colored reflections

            const whiteness = (r + g + b) / 3;
            if (whiteness >= t) {
              data[i + 3] = 0; // fully transparent
            } else if (soft > 0 && whiteness >= t - 2*soft) {
              const dist = t - whiteness;             // 0..(2*soft)
              const alpha = 255 * (dist / (2 * soft)); // fade in
              data[i + 3] = Math.min(data[i + 3], alpha);
            }
          }

          ctx.putImageData(imgData, 0, 0);
        } catch (e) {
          console.error("[RemoveWhiteBg] Unexpected error:", e);
        }
      };
      img.onerror = (e) => {
        console.error("[RemoveWhiteBg] Image failed to load:", this.src, e);
      };
      img.src = this.src;
    }
  }
};
</script>
